// hooks/useProjectPins.ts
// Per-user pinned-project ids for the sidebar/mobile project switchers.
// Persisted to localStorage keyed by user id (first cut — see #819 notes on
// upgrading to a backend user-prefs store for cross-device sync).
import { useCallback, useEffect, useState } from 'react'
import { useCurrentUser } from './useCurrentUser'

function storageKeyFor(userId: number | undefined): string {
  return `ui.sidebar.pinnedProjects.${userId ?? 'anon'}`
}

function readPins(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

export function useProjectPins() {
  const user = useCurrentUser()
  const storageKey = storageKeyFor(user?.id)
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => readPins(storageKey))

  useEffect(() => {
    setPinnedIds(readPins(storageKey))
  }, [storageKey])

  const togglePin = useCallback((id: string | number) => {
    const sid = String(id)
    setPinnedIds(prev => {
      const next = new Set(prev)
      if (next.has(sid)) next.delete(sid)
      else next.add(sid)
      try { localStorage.setItem(storageKey, JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }, [storageKey])

  const isPinned = useCallback((id: string | number) => pinnedIds.has(String(id)), [pinnedIds])

  return { isPinned, togglePin }
}
